<template>
  <span class="oe-root">
    <b-button
      size="sm"
      variant="outline-secondary"
      class="text-nowrap"
      :data-test="'outreach-open-' + site.siteId"
      @click="open = true"
    >
      <i class="fa fa-envelope-o mr-1"></i>Email
    </b-button>

    <b-modal
      v-model="open"
      lazy
      scrollable
      hide-footer
      no-close-on-backdrop
      size="lg"
      :title="'Email for ' + site.siteName"
    >
      <div class="oe" data-test="outreach-editor">
        <p class="text-muted oe-note mb-2" data-test="outreach-nothing-sent">
          Nothing is sent until you press Send and choose who gets it. Saving only stores the words. Choosing
          "Send to me" emails a test to your own sign-in address.
        </p>

        <div v-if="loading" class="text-muted py-3" data-test="outreach-loading">
          <i class="fa fa-spinner fa-spin mr-1"></i>Loading the email wording.
        </div>

        <b-alert v-if="loadError" show variant="danger" class="py-2 oe-note" data-test="outreach-load-error">
          {{ loadError }}
          <b-button size="sm" variant="link" class="p-0 ml-2" @click="load">Try again</b-button>
        </b-alert>

        <template v-if="current">
          <label class="oe-label" :for="uid + '-template'">Which email</label>
          <select :id="uid + '-template'" v-model="templateKey" class="form-control oe-field" data-test="outreach-template">
            <option v-for="t in templates" :key="t.key" :value="t.key">{{ t.label }}</option>
          </select>
          <p class="oe-note text-muted mt-1 mb-1" data-test="outreach-template-description">{{ current.description }}</p>
          <p class="oe-note mb-3" data-test="outreach-saved-line">{{ savedText }}</p>

          <label class="oe-label" :for="uid + '-subject'">Subject</label>
          <input
            :id="uid + '-subject'"
            ref="subject"
            v-model="draft.subject"
            type="text"
            class="form-control oe-field"
            :maxlength="limits.subjectMax"
            autocomplete="off"
            data-test="outreach-subject"
            @focus="focusField = 'subject'"
          >
          <div class="oe-count text-muted" data-test="outreach-subject-count">{{ subjectCount }}</div>

          <label class="oe-label mt-2" :for="uid + '-body'">Message</label>
          <div class="oe-chips" data-test="outreach-chips">
            <span class="oe-note text-muted mr-1">Add a detail about the practice:</span>
            <button
              v-for="v in current.variables"
              :key="v.key"
              type="button"
              class="oe-chip"
              :title="v.label + ', for example ' + v.example"
              :data-test="'outreach-chip-' + v.key"
              @mousedown.prevent
              @click="insert(v.token)"
            >{{ v.label }}</button>
          </div>
          <textarea
            :id="uid + '-body'"
            ref="body"
            v-model="draft.body"
            rows="10"
            class="form-control oe-field oe-body"
            :maxlength="limits.bodyMax"
            data-test="outreach-body"
            @focus="focusField = 'body'"
          ></textarea>
          <div class="oe-count text-muted" data-test="outreach-body-count">{{ bodyCount }}</div>
          <p class="oe-note text-muted mb-2">
            A blank line starts a new paragraph. The chips fill in the practice's own details when the email is made.
          </p>

          <div class="oe-preview" data-test="outreach-preview">
            <div class="d-flex justify-content-between align-items-center mb-2" style="gap: 8px;">
              <h6 class="mb-0">What {{ site.siteName }} would see</h6>
              <b-button size="sm" variant="link" class="p-0 text-nowrap" :disabled="preview.state === 'loading'" data-test="outreach-check-again" @click="runPreview">
                <i class="fa fa-refresh mr-1" :class="{ 'fa-spin': preview.state === 'loading' }"></i>Check again
              </b-button>
            </div>

            <b-alert v-if="preview.error" show variant="warning" class="py-1 oe-note" data-test="outreach-preview-error">{{ preview.error }}</b-alert>

            <div v-if="!shown && preview.state === 'loading'" class="text-muted oe-note" data-test="outreach-preview-loading">Checking.</div>

            <div v-if="shown" :class="{ 'oe-stale': preview.state === 'loading' || !previewIsCurrent }" data-test="outreach-preview-body">
              <ul v-if="problems.length" class="oe-problems" data-test="outreach-problems">
                <li v-for="(p, i) in problems" :key="i">{{ p.message }}</li>
              </ul>
              <div v-else-if="shown.renderError" class="oe-note text-danger" data-test="outreach-render-error">{{ shown.renderError.message }}</div>
              <div v-else-if="shown.rendered" class="oe-mail">
                <div class="oe-mail-subject" data-test="outreach-rendered-subject">{{ shown.rendered.subject }}</div>
                <div class="oe-mail-body" data-test="outreach-rendered-body">{{ shown.rendered.text }}</div>
              </div>

              <p v-if="recipients" class="oe-note mt-2 mb-1" :class="recipients.tone === 'warn' ? 'text-danger' : 'text-muted'" data-test="outreach-recipients">{{ recipients.text }}</p>
              <div v-if="eligibility" class="oe-note" data-test="outreach-eligibility">
                <strong :class="eligibility.tone === 'ok' ? 'text-success' : 'text-warning-dark'">{{ eligibility.headline }}</strong>
                <ul v-if="eligibility.reasons.length" class="oe-reasons mb-0">
                  <li v-for="(r, i) in eligibility.reasons" :key="i">{{ r }}</li>
                </ul>
                <p v-if="dirty && current.latest" class="text-muted mt-1 mb-0" data-test="outreach-eligibility-saved-note">
                  This check uses the saved words, not the changes you have not saved yet.
                </p>
              </div>
            </div>
          </div>

          <b-alert v-if="saveMessages.length" show variant="danger" class="py-2 mt-3 mb-0 oe-note" data-test="outreach-save-error">
            <div v-for="(m, i) in saveMessages" :key="i">{{ m }}</div>
          </b-alert>
          <b-alert v-if="savedNote" show variant="success" class="py-2 mt-3 mb-0 oe-note" data-test="outreach-saved-note">{{ savedNote }}</b-alert>

          <b-alert v-if="sendMessages.length" show :variant="sendWarning ? 'warning' : 'danger'" class="py-2 mt-3 mb-0 oe-note" :data-test="sendWarning ? 'outreach-send-unknown' : 'outreach-send-error'">
            <div v-for="(m, i) in sendMessages" :key="i">{{ m }}</div>
          </b-alert>
          <b-alert v-if="sendNote" show variant="success" class="py-2 mt-3 mb-0 oe-note" data-test="outreach-send-note">{{ sendNote }}</b-alert>

          <!-- The Send choice. Nothing is chosen when it opens, and Send stays off until something is. -->
          <div v-if="sendOpen" class="oe-send" data-test="outreach-send-panel">
            <fieldset class="oe-audience" data-test="outreach-audience">
              <legend class="oe-label">Who should get this email?</legend>
              <label class="oe-radio">
                <input v-model="audience" type="radio" :name="uid + '-audience'" value="me" :disabled="sending" data-test="outreach-audience-me">
                <span>
                  <strong>Send to me</strong>
                  <span class="oe-note text-muted d-block">A test, to your own sign-in address only: the words in the boxes, filled in for {{ site.siteName }}. It never goes to the practice.</span>
                </span>
              </label>
              <label class="oe-radio">
                <input v-model="audience" type="radio" :name="uid + '-audience'" value="practice" :disabled="sending" data-test="outreach-audience-practice">
                <span>
                  <strong>Send to the practice's admins</strong>
                  <span class="oe-note text-muted d-block">The saved words, to the people at {{ site.siteName }} who are Admins. You confirm the exact number before anything goes.</span>
                </span>
              </label>
            </fieldset>

            <p v-if="audience === 'practice' && practiceOff" class="oe-note text-muted mb-2" data-test="outreach-practice-off-note">
              Sending to practices is switched off for now. If you go on, the server will say so and nothing will be sent.
            </p>
            <ul v-if="audience === 'practice' && !practice.ok && !confirming" class="oe-reasons mb-2" data-test="outreach-practice-blockers">
              <li v-for="(r, i) in practice.reasons" :key="i">{{ r }}</li>
            </ul>

            <div v-if="confirming" class="oe-confirm" data-test="outreach-confirm">
              <p class="mb-1" data-test="outreach-confirm-line"><strong>{{ confirmLine }}</strong></p>
              <p v-if="current.latest" class="oe-note text-muted mb-2" data-test="outreach-confirm-subject">Subject: {{ current.latest.subject }}. The email is shown above.</p>
              <div class="oe-actions">
                <b-button variant="outline-secondary" :disabled="sending" data-test="outreach-confirm-back" @click="confirming = false">Go back</b-button>
                <b-button variant="danger" :disabled="sending || !practice.ok" data-test="outreach-confirm-yes" @click="confirmPractice">
                  {{ sending ? 'Sending…' : confirmButtonText }}
                </b-button>
              </div>
            </div>

            <div v-else class="oe-actions">
              <b-button variant="primary" :disabled="!canSend" data-test="outreach-send" @click="send">
                <i class="fa fa-paper-plane-o mr-1"></i>{{ sending ? 'Sending…' : sendButtonText }}
              </b-button>
            </div>
          </div>

          <div class="oe-footer">
            <span v-if="dirty" class="oe-note text-muted" data-test="outreach-unsaved">You have changes that are not saved.</span>
            <b-button variant="outline-secondary" data-test="outreach-close" @click="open = false">Close</b-button>
            <b-button variant="outline-primary" :disabled="sending" data-test="outreach-send-open" :title="sendOpen ? 'Put the Send choice away' : 'Choose who gets this email'" @click="toggleSend">
              <i class="fa fa-paper-plane-o mr-1"></i>{{ sendOpen ? 'Cancel send' : 'Send…' }}
            </b-button>
            <b-button variant="primary" :disabled="!canSave" data-test="outreach-save" @click="save">
              {{ saving ? 'Saving…' : 'Save these words' }}
            </b-button>
          </div>
          <p class="oe-note text-muted oe-test-hint mb-0" data-test="outreach-send-hint">
            "Send…" lets you choose who gets this email. Nothing is chosen for you, and nothing is sent until you press Send.
          </p>
        </template>
      </div>
    </b-modal>
  </span>
</template>

<script>
import { fetchOutreachTemplates, previewOutreach, saveOutreachTemplate, sendOutreach } from '@/api/outreach';
import { NO_TOKEN_MESSAGE } from '@/api/customerHealth';
import { extractApiError } from '@/utils/apiError';
import { spliceAtCursor } from '@/utils/textInsert.js';
import { formatDay } from '@/utils/customerHealth';
import {
  PREVIEW_DELAY_MS, suggestTemplateKey, startingDraft, sameWords, countText, savedLine, recipientsLine, eligibilityView, saveFailure,
  testSentLine, testFailure, AUDIENCE, audienceChoice, practiceReadiness, practiceConfirmLine, practiceSentLine,
} from '@/utils/outreachEditor';

const LOAD_FAILED = "Couldn't load the email wording. Please try again.";
const BAD_SHAPE = 'The email wording came back in an unexpected shape. Please try again.';
const PREVIEW_FAILED = "Couldn't check the preview. Please try again.";
const SAVE_FAILED = "Couldn't save the words. Nothing was changed. Please try again.";
const BAD_SAVE = 'The save came back in an unexpected shape, so we cannot tell whether it worked. Press Check again to see what is saved.';
const SEND_FAILED = "Couldn't send. Nothing was sent. Please try again.";
const BAD_TEST = 'The test came back in an unexpected shape, so we cannot tell whether it was sent. Check your inbox before trying again.';
const BAD_PRACTICE = "The answer came back in an unexpected shape, so we cannot tell whether it went to the practice. It will not be sent twice: the server keeps a record of each send. Press Check again to see where it stands.";

const IDLE_PREVIEW = { state: 'idle', data: null, error: null, signature: null };

export default {
  name: 'OutreachEmailEditor',
  props: {
    // One row of the Customer Health report (the `site` the panel's row-actions slot hands over).
    site: { type: Object, required: true },
  },
  data() {
    return {
      open: false,
      loading: false,
      loadError: null,
      templates: [],
      limits: null,
      drafts: {},          // what is typed, per template, so switching emails never loses edits
      templateKey: null,
      focusField: 'body',  // which box the chips add to: the one last clicked into
      preview: { ...IDLE_PREVIEW },
      saving: false,
      saveMessages: [],
      savedNote: null,
      practiceSendEnabled: null, // what the server last said about emailing practices (true / false / null = not told); a note only
      sendOpen: false,     // the Send choice is showing
      audience: null,      // NOTHING is preselected: only 'me' or 'practice', chosen by the person
      confirming: false,   // the practice confirm step (the exact number, then one more click)
      sending: false,
      sendMessages: [],
      sendWarning: false,  // true when the outcome is UNKNOWN (it may have gone out): shown as a warning, not an error
      sendNote: null,
      timer: null,
      loadSeq: 0,          // bumped per request and on destroy, so a late answer never overwrites a newer one
      previewSeq: 0,
      saveSeq: 0,
      sendSeq: 0,
    };
  },
  computed: {
    uid() { return `oe-${this._uid}`; },
    current() { return this.templates.find((t) => t.key === this.templateKey) || null; },
    draft() { return this.drafts[this.templateKey] || { subject: '', body: '' }; },
    baseline() { return this.current ? startingDraft(this.current) : null; },
    dirty() { return !!this.baseline && !sameWords(this.draft, this.baseline); },
    signature() { return JSON.stringify([this.templateKey, this.draft.subject, this.draft.body]); },
    previewIsCurrent() { return this.preview.signature === this.signature; },
    shown() { return this.preview.data; },
    problems() { return this.shown && this.shown.validation && !this.shown.validation.ok ? this.shown.validation.problems || [] : []; },
    recipients() { return this.shown ? recipientsLine(this.shown.recipients) : null; },
    eligibility() { return this.shown ? eligibilityView(this.shown.eligibility) : null; },
    savedText() {
      const latest = this.current && this.current.latest;
      return savedLine(latest, latest ? formatDay(latest.savedAt) : null);
    },
    subjectCount() { return countText(this.draft.subject, this.limits && this.limits.subjectMax); },
    bodyCount() { return countText(this.draft.body, this.limits && this.limits.bodyMax); },
    // The server is the validator; this only keeps the button honest. It needs a preview of THESE words that the
    // server accepted, and (once something is saved) words that differ from the saved version.
    canSave() {
      if (this.saving || !this.current || !this.previewIsCurrent || this.preview.state !== 'ready') return false;
      if (!this.shown || !this.shown.validation || this.shown.validation.ok !== true) return false;
      return !(this.current.latest && sameWords(this.draft, this.current.latest));
    },
    // "Send to me" needs a preview of THESE words that the server accepted AND rendered for this practice (a practice
    // missing a detail the email needs cannot be tested). Unlike Save it does not need the words to differ from what is
    // saved, and it does not need them saved at all.
    canSendMe() {
      if (this.sending || this.saving || !this.current || !this.previewIsCurrent || this.preview.state !== 'ready') return false;
      if (!this.shown || !this.shown.validation || this.shown.validation.ok !== true) return false;
      return !!this.shown.rendered && !this.shown.renderError;
    },
    // "Send to the practice's admins" is a different thing: the practice gets the SAVED words, so it needs saved,
    // unchanged words, a practice that qualifies today, and a real number of people (utils/outreachEditor.js
    // practiceReadiness). The server re-checks every one of these; this only keeps the button honest.
    practice() {
      return practiceReadiness({ preview: this.preview, signature: this.signature, latest: this.current && this.current.latest, draft: this.draft });
    },
    practiceOff() { return this.practiceSendEnabled === false; },
    // Nothing is chosen until the person chooses; Send stays off until what is chosen could work.
    canSend() {
      if (this.sending || this.saving || this.confirming) return false;
      const audience = audienceChoice(this.audience);
      if (audience === AUDIENCE.ME) return this.canSendMe;
      if (audience === AUDIENCE.PRACTICE) return this.practice.ok;
      return false;
    },
    sendButtonText() {
      if (this.audience === AUDIENCE.ME) return 'Send to me';
      if (this.audience === AUDIENCE.PRACTICE) return 'Review and send…';
      return 'Send';
    },
    confirmLine() { return this.practice.ok ? practiceConfirmLine(this.site.siteName, this.practice.count, this.practice.version) : ''; },
    confirmButtonText() {
      if (!this.practice.ok) return 'Yes, send';
      return this.practice.count === 1 ? 'Yes, email 1 person' : `Yes, email ${this.practice.count} people`;
    },
  },
  watch: {
    open(isOpen) { if (isOpen && !this.templates.length && !this.loading) this.load(); },
    // Declared BEFORE `signature` on purpose: a template switch drops the old template's preview (and any late
    // answer for it) first, then the signature watcher asks for the new one straight away.
    templateKey() {
      this.previewSeq += 1;
      this.preview = { ...IDLE_PREVIEW };
      this.confirming = false;
      this.audience = null;      // a different email: the choice starts again, with nothing chosen
    },
    signature() {
      this.saveMessages = [];
      this.savedNote = null;
      this.sendMessages = [];
      this.sendNote = null;
      this.confirming = false;   // other words than the ones the person was about to confirm
      if (this.current) this.schedulePreview();
    },
    audience() { this.confirming = false; },
  },
  beforeDestroy() {
    clearTimeout(this.timer);
    this.loadSeq += 1;
    this.previewSeq += 1;
    this.saveSeq += 1;
    this.sendSeq += 1;
  },
  methods: {
    auth() { return this.$store.state.getAuth ? this.$store.state.getAuth() : null; },
    messageFor(e, fallback) { return e && e.message === NO_TOKEN_MESSAGE ? NO_TOKEN_MESSAGE : extractApiError(e, fallback); },

    async load() {
      const mine = ++this.loadSeq;
      this.loading = true;
      this.loadError = null;
      try {
        const data = await fetchOutreachTemplates({ auth: this.auth() });
        if (mine !== this.loadSeq) return;
        const ok = data && Array.isArray(data.templates) && data.templates.length && data.limits
          && data.templates.every((t) => t && typeof t.key === 'string' && Array.isArray(t.variables) && t.starting);
        if (!ok) { this.loadError = BAD_SHAPE; return; }
        const drafts = {};
        data.templates.forEach((t) => { drafts[t.key] = startingDraft(t); });
        this.limits = data.limits;
        this.practiceSendEnabled = data.practiceSend && typeof data.practiceSend.enabled === 'boolean' ? data.practiceSend.enabled : null;
        this.drafts = drafts;
        this.templates = data.templates;
        const wanted = suggestTemplateKey(this.site);
        this.templateKey = data.templates.some((t) => t.key === wanted) ? wanted : data.templates[0].key;
      } catch (e) {
        if (mine !== this.loadSeq) return;
        this.loadError = this.messageFor(e, LOAD_FAILED);
      } finally {
        if (mine === this.loadSeq) this.loading = false;
      }
    },

    // The first preview is immediate; after that each keystroke waits a moment so typing is not one request per letter.
    schedulePreview() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.runPreview(), this.preview.state === 'idle' ? 0 : PREVIEW_DELAY_MS);
    },

    async runPreview() {
      if (!this.current) return;
      clearTimeout(this.timer);
      const mine = ++this.previewSeq;
      const signature = this.signature;
      this.preview = { ...this.preview, state: 'loading', error: null };
      try {
        const data = await previewOutreach({
          auth: this.auth(), templateKey: this.templateKey, siteId: this.site.siteId, subject: this.draft.subject, body: this.draft.body,
        });
        if (mine !== this.previewSeq) return;
        const ok = data && data.validation && Array.isArray(data.validation.problems);
        if (!ok) { this.preview = { ...this.preview, state: 'error', error: PREVIEW_FAILED }; return; }
        this.preview = { state: 'ready', data, error: null, signature };
      } catch (e) {
        if (mine !== this.previewSeq) return;
        this.preview = { ...this.preview, state: 'error', error: this.messageFor(e, PREVIEW_FAILED) };
      }
    },

    // Add a variable chip where the cursor is in the box last clicked into (the existing src/utils/textInsert.js).
    insert(token) {
      const field = this.focusField === 'subject' ? 'subject' : 'body';
      const el = this.$refs[field];
      const { text, caret } = spliceAtCursor(this.draft[field], el ? el.selectionStart : null, el ? el.selectionEnd : null, token);
      this.draft[field] = text;
      this.$nextTick(() => { if (el) { el.focus(); el.setSelectionRange(caret, caret); } });
    },

    async save() {
      if (!this.canSave) return;
      const mine = ++this.saveSeq;
      const key = this.templateKey;
      const sent = { subject: this.draft.subject, body: this.draft.body };
      this.saving = true;
      this.saveMessages = [];
      this.savedNote = null;
      try {
        const data = await saveOutreachTemplate({ auth: this.auth(), templateKey: key, subject: sent.subject, body: sent.body });
        if (mine !== this.saveSeq) return;
        const saved = data && data.saved;
        // Never report a save we cannot read back.
        if (!saved || !Number.isInteger(saved.version)) { this.saveMessages = [BAD_SAVE]; return; }
        this.templates = this.templates.map((t) => (t.key === key ? { ...t, latest: saved } : t));
        // The server tidies the words it stores (line endings, spaces at the ends). Show exactly what was stored, unless
        // more was typed while the save ran, so the screen never claims "unsaved changes" right after a good save.
        if (sameWords(this.drafts[key], sent) && typeof saved.subject === 'string' && typeof saved.body === 'string') {
          this.drafts = { ...this.drafts, [key]: { subject: saved.subject, body: saved.body } };
        }
        // Let the words-changed watcher run first (it clears old notes and would schedule a second preview), then
        // announce the save and ask for the one preview that follows it.
        await this.$nextTick();
        if (mine !== this.saveSeq) return;
        this.savedNote = `Saved as version ${saved.version}. Nothing has been sent.`;
        this.runPreview();
      } catch (e) {
        if (mine !== this.saveSeq) return;
        const failure = saveFailure(e);
        this.saveMessages = failure.kind === 'other' ? [this.messageFor(e, SAVE_FAILED)] : failure.messages;
        if (failure.kind === 'conflict') await this.refreshSaved(mine);
      } finally {
        if (mine === this.saveSeq) this.saving = false;
      }
    },

    toggleSend() {
      this.sendOpen = !this.sendOpen;
      this.audience = null;      // nothing is ever preselected
      this.confirming = false;
      this.sendMessages = [];
      this.sendNote = null;
    },

    // The Send button. "Send to me" goes straight out (it only ever reaches the signed-in person). "Send to the practice's
    // admins" does NOT: it opens the confirm step with the exact number, and only that step's button sends.
    async send() {
      if (!this.canSend) return;
      if (audienceChoice(this.audience) === AUDIENCE.PRACTICE) { this.confirming = true; return; }
      await this.deliver(AUDIENCE.ME);
    },

    async confirmPractice() {
      if (!this.confirming || this.sending || !this.practice.ok) return;
      await this.deliver(AUDIENCE.PRACTICE);
    },

    // Emails through the one send route. The request carries the audience, the template and the practice, plus: for 'me' the
    // typed words (the server takes the address from the verified token), for 'practice' the number and saved version the
    // person confirmed (the server sends the SAVED words to the practice's own Admins). Never an address.
    // A success is announced only when the answer says so in full; an unclear outcome is a WARNING ("it may have gone out"),
    // never "failed" and never "sent".
    async deliver(audience) {
      const chosen = audienceChoice(audience);
      if (!chosen) return;
      const mine = ++this.sendSeq;
      const asked = chosen === AUDIENCE.PRACTICE ? { count: this.practice.count, version: this.practice.version } : null;
      this.sending = true;
      this.sendMessages = [];
      this.sendWarning = false;
      this.sendNote = null;
      try {
        const data = await sendOutreach(chosen === AUDIENCE.PRACTICE
          ? { auth: this.auth(), audience: chosen, templateKey: this.templateKey, siteId: this.site.siteId, expectedRecipients: asked.count, expectedVersion: asked.version }
          : { auth: this.auth(), audience: chosen, templateKey: this.templateKey, siteId: this.site.siteId, subject: this.draft.subject, body: this.draft.body });
        if (mine !== this.sendSeq) return;
        const line = chosen === AUDIENCE.PRACTICE ? practiceSentLine(data, this.site.siteName) : testSentLine(data);
        if (!line) {
          this.sendMessages = [chosen === AUDIENCE.PRACTICE ? BAD_PRACTICE : BAD_TEST];
          this.sendWarning = true;
          this.confirming = false;
          return;
        }
        this.sendNote = line;
        if (chosen === AUDIENCE.PRACTICE) {
          // It went. Put the choice away so a second click cannot repeat it, and re-check: the preview now says it was sent.
          this.confirming = false;
          this.audience = null;
          await this.$nextTick();
          if (mine === this.sendSeq) this.runPreview();
        }
      } catch (e) {
        if (mine !== this.sendSeq) return;
        const failure = testFailure(e);
        this.sendWarning = failure.kind === 'unknown_outcome';
        this.sendMessages = failure.kind === 'other' ? [this.messageFor(e, SEND_FAILED)] : failure.messages;
        this.confirming = false;
        // It MAY have gone to the practice: put the choice away so it is not repeated by a second click.
        if (chosen === AUDIENCE.PRACTICE && this.sendWarning) this.audience = null;
      } finally {
        if (mine === this.sendSeq) this.sending = false;
      }
    },

    // After a lost race: re-read what is saved, and keep the typed words in the boxes.
    async refreshSaved(mine) {
      try {
        const data = await fetchOutreachTemplates({ auth: this.auth() });
        if (mine !== this.saveSeq || !data || !Array.isArray(data.templates)) return;
        const latestByKey = Object.fromEntries(data.templates.map((t) => [t.key, t.latest || null]));
        this.templates = this.templates.map((t) => (t.key in latestByKey ? { ...t, latest: latestByKey[t.key] } : t));
        this.runPreview();
      } catch (e) { /* the message above already says what happened; Check again retries */ }
    },
  },
};
</script>

<style scoped>
.oe-note { font-size: 0.85rem; }
.oe-label { display: block; margin-bottom: 4px; font-size: 0.78rem; font-weight: 600; color: #475569; text-transform: uppercase; letter-spacing: 0.03em; }
.oe-field { width: 100%; }
.oe-body { min-height: 190px; line-height: 1.45; resize: vertical; }
.oe-count { margin-top: 2px; font-size: 0.75rem; text-align: right; }
.oe-chips { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-bottom: 6px; }
.oe-chip { padding: 2px 10px; border: 1px solid #bfdbfe; border-radius: 12px; background: #eff6ff; color: #1d4ed8; font-size: 0.8rem; cursor: pointer; }
.oe-chip:hover { background: #dbeafe; }
@media (max-width: 576px) {
  .oe-chips { gap: 8px; }
  .oe-chip { min-height: 36px; padding: 6px 14px; font-size: 0.85rem; }
}
.oe-preview { margin-top: 12px; padding: 12px; border: 1px solid #e2e8f0; border-radius: 8px; background: #f8fafc; }
.oe-mail { border: 1px solid #e2e8f0; border-radius: 6px; background: #fff; }
.oe-mail-subject { padding: 8px 12px; border-bottom: 1px solid #e2e8f0; font-weight: 600; word-break: break-word; }
.oe-mail-body { padding: 10px 12px; white-space: pre-wrap; word-break: break-word; font-size: 0.9rem; }
.oe-stale { opacity: 0.55; }
.oe-problems, .oe-reasons { margin: 0 0 0 1.1rem; padding: 0; font-size: 0.85rem; }
.oe-problems { color: #b91c1c; }
.text-warning-dark { color: #92400e; }
.oe-footer { display: flex; flex-wrap: wrap; align-items: center; justify-content: flex-end; gap: 8px; margin-top: 14px; }
.oe-footer > .oe-note { margin-right: auto; }
.oe-test-hint { margin-top: 8px; text-align: right; }
.oe-send { margin-top: 14px; padding: 12px; border: 1px solid #e2e8f0; border-radius: 8px; background: #f8fafc; }
.oe-audience { margin: 0 0 8px; padding: 0; border: 0; min-width: 0; }
.oe-audience legend { float: none; width: auto; padding: 0; }
.oe-radio { display: flex; align-items: flex-start; gap: 10px; margin: 0 0 8px; padding: 8px 10px; border: 1px solid #e2e8f0; border-radius: 6px; background: #fff; cursor: pointer; }
.oe-radio input { flex: 0 0 auto; width: 18px; height: 18px; margin-top: 2px; }
.oe-confirm { padding: 10px 12px; border: 1px solid #fca5a5; border-radius: 6px; background: #fef2f2; }
.oe-actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 8px; }
/* LAST in the file on purpose: a media rule only wins over the base rule above when it comes AFTER it (same specificity).
   The app's floating chat bubble sits over the bottom-right corner of every page, so on a phone the buttons start at
   the left, where the bubble can never cover them. Pinned by cypress/node-tests/outreach-editor.test.js. */
@media (max-width: 576px) {
  .oe-footer { justify-content: flex-start; }
  .oe-actions { justify-content: flex-start; }
  .oe-test-hint { text-align: left; }
}
</style>
